import { useEffect, useState, type FormEvent } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  formaterIdentite,
  formaterQuantite,
  transitionOrdrePermise,
  type CategorieOpcvm,
} from '@gsm/partage';
import { api, ErreurApi } from '../api/client';
import { messageErreur, useRessource } from '../api/ressource';
import { useAuth } from '../auth/contexte';
import { Bloc, Tableau, type Colonne } from '../composants/Tableau';
import { BandeauAlerte } from '../composants/BandeauAlerte';
import { Champ } from '../composants/Champ';
import { Icone } from '../composants/Icone';
import { Modale } from '../composants/Modale';
import { Pastille } from '../composants/Pastille';
import { libelleStatutOrdre, statutOrdre } from '../composants/statutOrdre';
import { Nombre } from '../composants/Nombre';
import { LienFiche } from '../composants/LienAction';
import { ModaleFonds } from '../composants/ModaleFonds';
import {
  BarreFiltres,
  FiltreChoix,
  FiltreSegments,
  Pagination,
  type PageDe,
} from '../composants/Filtres';

interface Ordre {
  id: string;
  reference: string;
  canal: string;
  sens: string;
  mode: string;
  statut: string;
  quantiteDemandee: string | null;
  montantDemande: string | null;
  montantEstime: string;
  quantiteRealisee: string | null;
  montantRealise: string | null;
  instrumentConfirme: boolean;
  instrumentId: string | null;
  creePar: string;
  mandat: {
    id: string;
    reference: string;
    client: { nom: string; prenom: string };
    profil: { libelle: string; charia: boolean };
  };
  instrument: { libelle: string; opcvm: { categorie: CategorieOpcvm } | null } | null;
  tranche: { libelle: string } | null;
}

interface MandatFiltre {
  id: string;
  reference: string;
  statut: string;
  client: { nom: string; prenom: string };
}

interface InstrumentDisponible {
  id: string;
  code: string;
  libelle: string;
  type: 'ACTION' | 'BDT' | 'OPCVM';
  chariaCompliant: boolean;
}

interface TrancheDisponible {
  id: string;
  code: string;
  libelle: string;
}

const CANAUX = [
  { code: 'BOURSE', libelle: 'Société de bourse — actions' },
  { code: 'SALLE_MARCHES', libelle: 'Salle des marchés — BDT' },
  { code: 'OPCVM', libelle: 'Sociétés de gestion — OPCVM' },
];

/** Ordres qui attendent encore une action : l'écran s'ouvre sur eux. */
const EN_COURS = 'BROUILLON,A_VALIDER,VALIDE,TRANSMIS';

const FILTRES_STATUT = [
  [EN_COURS, 'En cours'],
  ...['BROUILLON', 'A_VALIDER', 'VALIDE', 'TRANSMIS', 'DENOUE', 'REJETE', 'ANNULE'].map(
    (s) => [s, libelleStatutOrdre(s)] as const,
  ),
  ['', 'Tous'],
] as const;

const TAILLE_PAGE = 50;

const TYPE_ATTENDU: Record<string, InstrumentDisponible['type']> = {
  BOURSE: 'ACTION',
  SALLE_MARCHES: 'BDT',
  OPCVM: 'OPCVM',
};

const SAISIE_VIDE = {
  mandatId: '',
  canal: 'BOURSE',
  sens: 'ACHAT',
  instrumentId: '',
  trancheMaturiteId: '',
  quantiteDemandee: '',
  montantDemande: '',
  coursIndicatif: '',
};

/**
 * Un achat BDT porte une tranche et un montant, jamais une ligne : elle est
 * choisie par la salle des marchés et découverte à l'exécution (RG-17).
 */
function estAchatBdt(canal: string, sens: string): boolean {
  return canal === 'SALLE_MARCHES' && sens === 'ACHAT';
}

/** Les actions et les cessions BDT se traitent en titres, le reste en MAD. */
function enQuantite(canal: string, sens: string): boolean {
  return canal === 'BOURSE' || (canal === 'SALLE_MARCHES' && sens === 'VENTE');
}

export function Ordres() {
  const { profil, aRole } = useAuth();
  // Un lien peut arriver filtré sur un mandat (Sorties de fonds › Voir les
  // ordres) ou sur un statut (tuile du tableau de bord).
  const [requeteUrl] = useSearchParams();
  const [filtre, setFiltre] = useState(() => requeteUrl.get('statut') ?? EN_COURS);
  const [canalFiltre, setCanalFiltre] = useState('');
  const [mandatFiltre, setMandatFiltre] = useState(() => requeteUrl.get('mandatId') ?? '');
  const [page, setPage] = useState(1);

  const requete = new URLSearchParams({ page: String(page), taille: String(TAILLE_PAGE) });
  if (filtre) requete.set('statut', filtre);
  if (canalFiltre) requete.set('canal', canalFiltre);
  if (mandatFiltre) requete.set('mandatId', mandatFiltre);

  const { donnees, chargement, erreur, recharger } = useRessource<
    PageDe<Ordre> & { fondsAConfirmer: number }
  >(`/ordres?${requete.toString()}`);
  const mandats = useRessource<MandatFiltre[]>('/mandats');
  const [selection, setSelection] = useState<Set<string>>(new Set());
  const [erreurAction, setErreurAction] = useState<string | null>(null);
  const [occupe, setOccupe] = useState(false);

  // Substitution de fonds (RG-16), fenêtre commune à la fiche campagne.
  const [substitution, setSubstitution] = useState<Ordre | null>(null);

  // Saisie manuelle d'un ordre. Référentiels chargés à l'ouverture seulement.
  const [saisieOuverte, setSaisieOuverte] = useState(false);
  const [saisie, setSaisie] = useState(SAISIE_VIDE);
  const [erreurSaisie, setErreurSaisie] = useState<string | null>(null);
  const instruments = useRessource<InstrumentDisponible[]>(
    saisieOuverte ? '/instruments?statut=ACTIF' : null,
  );
  const tranches = useRessource<TrancheDisponible[]>(saisieOuverte ? '/tranches-maturite' : null);

  const ordres = donnees?.elements ?? [];

  // Une action en lot peut vider la dernière page : revenir sur la précédente
  // plutôt qu'afficher une page blanche au-delà du total.
  useEffect(() => {
    if (donnees && donnees.elements.length === 0 && donnees.total > 0 && page > 1) {
      setPage(Math.ceil(donnees.total / donnees.taille));
    }
  }, [donnees, page]);
  const peutGerer = aRole('GERANT', 'RESP_GESTION');
  const peutValider = aRole('RESP_GESTION');
  const peutConstater = aRole('MIDDLE_BO', 'RESP_GESTION');
  // Compté par le serveur sur tous les ordres, pas sur la seule page affichée.
  const aConfirmer = donnees?.fondsAConfirmer ?? 0;

  function basculer(id: string) {
    const copie = new Set(selection);
    if (copie.has(id)) copie.delete(id);
    else copie.add(id);
    setSelection(copie);
  }

  const toutSelectionne = ordres.length > 0 && ordres.every((o) => selection.has(o.id));
  const partiellement = !toutSelectionne && ordres.some((o) => selection.has(o.id));

  function basculerTout() {
    setSelection(toutSelectionne ? new Set() : new Set(ordres.map((o) => o.id)));
  }

  /**
   * Changer de filtre ou de page vide la sélection. Sans cela, une action en
   * lot porterait sur des ordres devenus invisibles — on croirait annuler les
   * trois brouillons affichés en en annulant douze.
   */
  function filtrer(action: () => void) {
    setSelection(new Set());
    setPage(1);
    action();
  }

  function changerPage(numero: number) {
    setSelection(new Set());
    setPage(numero);
  }

  async function agir(appel: () => Promise<unknown>) {
    setErreurAction(null);
    setOccupe(true);
    try {
      await appel();
      setSelection(new Set());
      await recharger();
    } catch (e) {
      setErreurAction(e instanceof ErreurApi ? messageErreur(e) : 'Opération impossible.');
    } finally {
      setOccupe(false);
    }
  }

  /**
   * Chaque action ne porte que sur les ordres sélectionnés qu'elle peut
   * traiter, et son libellé les compte. Le serveur refuse un lot dès qu'un
   * seul ordre y est hors de propos : sans ce tri, « Tout sélectionner » sous
   * le filtre « En cours », qui mêle quatre statuts, ne mènerait qu'au refus.
   * La sélection ne survit ni à un changement de filtre ni de page : tous les
   * ordres cochés sont donc dans la page affichée.
   */
  const selectionnes = ordres.filter((o) => selection.has(o.id));
  const fondsEnAttente = (o: Ordre) => o.canal === 'OPCVM' && !o.instrumentConfirme;
  const estAuteur = (o: Ordre) => o.creePar === profil?.id && !aRole('ADMIN');
  const retenus = {
    // Un fonds ne se confirme que sur un brouillon (RG-16).
    fonds: selectionnes.filter((o) => o.statut === 'BROUILLON' && fondsEnAttente(o)),
    soumettre: selectionnes.filter(
      (o) =>
        transitionOrdrePermise(o.statut, 'A_VALIDER') &&
        !fondsEnAttente(o) &&
        // RG-17 : un achat BDT n'est soumis qu'avec sa tranche.
        !(estAchatBdt(o.canal, o.sens) && !o.tranche),
    ),
    valider: selectionnes.filter(
      (o) => transitionOrdrePermise(o.statut, 'VALIDE') && !estAuteur(o),
    ),
    annuler: selectionnes.filter((o) => transitionOrdrePermise(o.statut, 'ANNULE')),
    nonExecute: selectionnes.filter((o) => transitionOrdrePermise(o.statut, 'REJETE')),
  };
  const idsDe = (lot: Ordre[]) => lot.map((o) => o.id);

  /** Infobulle d'un bouton qui laisse de côté une partie de la sélection. */
  function ecartes(lot: Ordre[], raison: string): string | undefined {
    const reste = selectionnes.length - lot.length;
    if (reste === 0) return undefined;
    return `${reste} ordre(s) sélectionné(s) laissé(s) de côté : ${raison}`;
  }

  const achatBdt = estAchatBdt(saisie.canal, saisie.sens);
  const parQuantite = enQuantite(saisie.canal, saisie.sens);
  const instrumentsDuCanal = (instruments.donnees ?? []).filter(
    (i) => i.type === TYPE_ATTENDU[saisie.canal],
  );

  function ouvrirSaisie() {
    setSaisie(SAISIE_VIDE);
    setErreurSaisie(null);
    setSaisieOuverte(true);
  }

  async function enregistrerSaisie(evenement: FormEvent) {
    evenement.preventDefault();
    setErreurSaisie(null);
    setOccupe(true);
    try {
      await api.post('/ordres', {
        mandatId: saisie.mandatId,
        canal: saisie.canal,
        sens: saisie.sens,
        // Le serveur refuse une ligne sur un achat BDT : ne pas l'envoyer du
        // tout plutôt que de compter sur lui pour l'ignorer.
        instrumentId: achatBdt ? undefined : saisie.instrumentId || undefined,
        trancheMaturiteId: achatBdt ? saisie.trancheMaturiteId || undefined : undefined,
        quantiteDemandee: parQuantite ? saisie.quantiteDemandee || undefined : undefined,
        montantDemande: parQuantite ? undefined : saisie.montantDemande || undefined,
        coursIndicatif: saisie.coursIndicatif || undefined,
      });
      setSaisieOuverte(false);
      await recharger();
    } catch (e) {
      setErreurSaisie(e instanceof ErreurApi ? messageErreur(e) : 'Création impossible.');
    } finally {
      setOccupe(false);
    }
  }

  const colonnes: Colonne<Ordre>[] = [
    {
      cle: 'choix',
      entete: (
        <input
          type="checkbox"
          checked={toutSelectionne}
          // L'état indéterminé ne s'exprime pas en JSX : il se pose sur le nœud.
          ref={(noeud) => {
            if (noeud) noeud.indeterminate = partiellement;
          }}
          onChange={basculerTout}
          disabled={ordres.length === 0}
          aria-label="Tout sélectionner"
        />
      ),
      rendu: (o) => (
        <input
          type="checkbox"
          checked={selection.has(o.id)}
          onChange={() => basculer(o.id)}
          aria-label={`Sélectionner ${o.reference}`}
        />
      ),
    },
    { cle: 'reference', entete: 'Référence', num: true, rendu: (o) => o.reference },
    {
      cle: 'mandat',
      entete: 'Mandat',
      rendu: (o) => (
        <LienFiche to={`/mandats/${o.mandat.id}`}>
          {formaterIdentite(o.mandat.client.nom, o.mandat.client.prenom)}
        </LienFiche>
      ),
    },
    {
      cle: 'sens',
      entete: 'Sens',
      rendu: (o) => (
        <span className={o.sens === 'ACHAT' ? 'ordre-achat' : 'ordre-vente'}>{o.sens}</span>
      ),
    },
    { cle: 'canal', entete: 'Canal', rendu: (o) => o.canal.replace('_', ' ').toLowerCase() },
    {
      cle: 'sousjacent',
      entete: 'Sous-jacent',
      rendu: (o) => (
        <>
          {o.instrument?.libelle ?? `BDT · ${o.tranche?.libelle ?? '—'}`}
          {o.canal === 'OPCVM' && !o.instrumentConfirme && (
            <>
              {' '}
              <Pastille ton="attente">Fonds à confirmer</Pastille>
            </>
          )}
          {/* Le brouillon reste modifiable même une fois le fonds confirmé :
              le gérant peut revenir sur son choix jusqu'à la soumission. */}
          {peutGerer && o.canal === 'OPCVM' && o.statut === 'BROUILLON' && (
            <>
              {' '}
              <button
                type="button"
                className="bouton bouton--compact bouton--fantome"
                onClick={() => setSubstitution(o)}
              >
                {o.instrumentConfirme ? 'Changer' : 'Choisir'}
              </button>
            </>
          )}
        </>
      ),
    },
    {
      cle: 'quantite',
      entete: 'Quantité / montant',
      num: true,
      rendu: (o) =>
        o.quantiteDemandee ? (
          formaterQuantite(o.quantiteDemandee)
        ) : (
          <Nombre valeur={o.montantDemande} />
        ),
    },
    {
      cle: 'montant',
      entete: 'Montant estimé',
      num: true,
      rendu: (o) => <Nombre valeur={o.montantEstime} />,
    },
    {
      cle: 'statut',
      entete: 'Statut',
      rendu: (o) => <Pastille ton={statutOrdre(o).ton}>{statutOrdre(o).libelle}</Pastille>,
    },
  ];

  return (
    <>
      <p className="entete__fil">Exécution</p>
      <div className="entete">
        <h1>Ordres</h1>
        <span className="entete__ref">{donnees ? `${donnees.total} ordres` : ''}</span>
      </div>

      {erreur && (
        <BandeauAlerte nature="refus" titre="Chargement impossible">
          {messageErreur(erreur)}
        </BandeauAlerte>
      )}
      {erreurAction && (
        <BandeauAlerte nature="refus" titre="Opération refusée">
          {erreurAction}
        </BandeauAlerte>
      )}

      {aConfirmer > 0 && (
        <BandeauAlerte nature="gestion" titre="Fonds OPCVM à confirmer">
          {aConfirmer} ordre(s) portent le fonds proposé par défaut. Tant que le gérant ne l’a pas
          confirmé, ils ne peuvent pas être soumis à validation (RG-16).
        </BandeauAlerte>
      )}

      <Bloc
        titre="Ordres"
        meta={chargement ? 'chargement…' : undefined}
        actions={
          peutGerer && (
            <button
              type="button"
              className="bouton bouton--icone bouton--compact"
              onClick={ouvrirSaisie}
            >
              <Icone nom="creer" taille={15} />
              Nouvel ordre
            </button>
          )
        }
      >
        <BarreFiltres
          actifs={filtre !== EN_COURS || canalFiltre !== '' || mandatFiltre !== ''}
          onRetirer={() =>
            filtrer(() => {
              setFiltre(EN_COURS);
              setCanalFiltre('');
              setMandatFiltre('');
            })
          }
        >
          <FiltreSegments
            libelle="Statut"
            valeur={filtre}
            onChange={(v) => filtrer(() => setFiltre(v))}
            options={FILTRES_STATUT}
          />
        </BarreFiltres>
        <BarreFiltres>
          <FiltreChoix
            libelle="Canal"
            tous="Tous les canaux"
            valeur={canalFiltre}
            onChange={(v) => filtrer(() => setCanalFiltre(v))}
            options={CANAUX.map((c) => [c.code, c.libelle] as const)}
          />
          <FiltreChoix
            libelle="Mandat"
            tous="Tous les mandats"
            valeur={mandatFiltre}
            onChange={(v) => filtrer(() => setMandatFiltre(v))}
            options={(mandats.donnees ?? []).map(
              (m) =>
                [
                  m.id,
                  `${m.reference} — ${formaterIdentite(m.client.nom, m.client.prenom)}`,
                ] as const,
            )}
          />
        </BarreFiltres>

        <Tableau colonnes={colonnes} lignes={ordres} cle={(o) => o.id} vide="Aucun ordre." />
        {donnees && (
          <Pagination
            page={donnees.page}
            taille={donnees.taille}
            total={donnees.total}
            onPage={changerPage}
          />
        )}

        {(peutGerer || peutConstater) && (
          <div className="barre-actions barre-actions--apres">
            {peutGerer && (
              <button
                className="bouton"
                disabled={occupe || retenus.fonds.length === 0}
                title={ecartes(retenus.fonds, 'seuls les brouillons OPCVM au fonds non confirmé.')}
                onClick={() =>
                  void agir(async () => {
                    for (const o of retenus.fonds) {
                      await api.post(`/ordres/${o.id}/confirmer-fonds`, {});
                    }
                  })
                }
              >
                Confirmer les fonds ({retenus.fonds.length})
              </button>
            )}
            {peutGerer && (
              <button
                className="bouton"
                disabled={occupe || retenus.soumettre.length === 0}
                title={ecartes(
                  retenus.soumettre,
                  'seuls les brouillons au fonds confirmé et, pour un achat BDT, à la tranche désignée.',
                )}
                onClick={() =>
                  void agir(() => api.post('/ordres/soumettre', { ids: idsDe(retenus.soumettre) }))
                }
              >
                Soumettre ({retenus.soumettre.length})
              </button>
            )}
            {peutValider && (
              <button
                className="bouton"
                disabled={occupe || retenus.valider.length === 0}
                title={ecartes(
                  retenus.valider,
                  'seuls les ordres à valider dont vous n’êtes pas l’auteur (RG-10).',
                )}
                onClick={() =>
                  void agir(() => api.post('/ordres/valider', { ids: idsDe(retenus.valider) }))
                }
              >
                Valider ({retenus.valider.length})
              </button>
            )}
            {peutGerer && (
              <button
                className="bouton bouton--fantome"
                disabled={occupe || retenus.annuler.length === 0}
                title={ecartes(
                  retenus.annuler,
                  'un ordre transmis au partenaire ou clos ne s’annule pas.',
                )}
                onClick={() => {
                  const lot = retenus.annuler;
                  const motif = window.prompt(`Motif d'annulation de ${lot.length} ordre(s)`);
                  if (motif)
                    void agir(() => api.post('/ordres/annuler', { ids: idsDe(lot), motif }));
                }}
              >
                Annuler ({retenus.annuler.length})
              </button>
            )}
            {/* Un ordre parti chez le partenaire ne s'annule pas : on constate
                qu'il n'a pas été servi. D'où deux boutons, pas un. */}
            {peutConstater && (
              <button
                className="bouton bouton--fantome"
                disabled={occupe || retenus.nonExecute.length === 0}
                title={ecartes(
                  retenus.nonExecute,
                  'seul un ordre transmis peut être constaté non exécuté.',
                )}
                onClick={() => {
                  const lot = retenus.nonExecute;
                  const motif = window.prompt(
                    `Motif de la non-exécution de ${lot.length} ordre(s)`,
                  );
                  if (motif)
                    void agir(() => api.post('/ordres/rejeter', { ids: idsDe(lot), motif }));
                }}
              >
                Non exécuté ({retenus.nonExecute.length})
              </button>
            )}
          </div>
        )}

        {selectionnes.length > 0 && (
          <p className="mention consigne--pied">
            {selectionnes.length} ordre(s) sélectionné(s). Chaque action ne porte que sur ceux
            qu’elle peut traiter, comptés entre parenthèses : le survol d’un bouton dit lesquels
            elle laisse de côté.
          </p>
        )}
      </Bloc>

      <Modale
        ouverte={saisieOuverte}
        onFermer={() => setSaisieOuverte(false)}
        surtitre="Hors proposition du moteur"
        titre="Nouvel ordre"
        taille="large"
      >
        {erreurSaisie && (
          <BandeauAlerte nature="refus" titre="Création refusée">
            {erreurSaisie}
          </BandeauAlerte>
        )}
        <p className="consigne consigne--tete">
          Pour les gestes que l’allocation cible ne décrit pas : solder une ligne sortie de
          l’univers, saisir une opportunité, corriger une position. L’ordre naît en brouillon et
          suit le même cycle que les autres — rien n’est court-circuité, seule son origine change.
        </p>

        <form onSubmit={(e) => void enregistrerSaisie(e)} noValidate>
          <div className="champ">
            <label className="surtitre" htmlFor="mandat-ordre">
              Mandat
            </label>
            <select
              id="mandat-ordre"
              className="champ__select"
              value={saisie.mandatId}
              onChange={(e) => setSaisie({ ...saisie, mandatId: e.target.value })}
              required
            >
              <option value="">—</option>
              {(mandats.donnees ?? [])
                // Un mandat qui n'est pas en gestion ne reçoit aucun ordre (RG-06).
                .filter((m) => m.statut === 'ACTIF')
                .map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.reference} — {formaterIdentite(m.client.nom, m.client.prenom)}
                  </option>
                ))}
            </select>
          </div>

          <div className="champs-en-ligne">
            <div className="champ champ--double">
              <label className="surtitre" htmlFor="canal-ordre">
                Canal
              </label>
              <select
                id="canal-ordre"
                className="champ__select"
                value={saisie.canal}
                onChange={(e) =>
                  setSaisie({
                    ...saisie,
                    canal: e.target.value,
                    instrumentId: '',
                    trancheMaturiteId: '',
                  })
                }
              >
                {CANAUX.map((c) => (
                  <option key={c.code} value={c.code}>
                    {c.libelle}
                  </option>
                ))}
              </select>
            </div>
            <div className="champ">
              <label className="surtitre" htmlFor="sens-ordre">
                Sens
              </label>
              <select
                id="sens-ordre"
                className="champ__select"
                value={saisie.sens}
                onChange={(e) => setSaisie({ ...saisie, sens: e.target.value, instrumentId: '' })}
              >
                <option value="ACHAT">Achat</option>
                <option value="VENTE">Vente</option>
              </select>
            </div>
          </div>

          {achatBdt ? (
            <div className="champ">
              <label className="surtitre" htmlFor="tranche-ordre">
                Tranche de maturité
              </label>
              <select
                id="tranche-ordre"
                className="champ__select"
                value={saisie.trancheMaturiteId}
                onChange={(e) => setSaisie({ ...saisie, trancheMaturiteId: e.target.value })}
                required
              >
                <option value="">—</option>
                {(tranches.donnees ?? []).map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.libelle}
                  </option>
                ))}
              </select>
              <span className="champ__aide">
                Un achat BDT porte une tranche et un montant : la ligne est choisie par la salle des
                marchés et découverte à l’exécution.
              </span>
            </div>
          ) : (
            <div className="champ">
              <label className="surtitre" htmlFor="instrument-ordre">
                Instrument
              </label>
              <select
                id="instrument-ordre"
                className="champ__select"
                value={saisie.instrumentId}
                onChange={(e) => setSaisie({ ...saisie, instrumentId: e.target.value })}
                required
              >
                <option value="">—</option>
                {instrumentsDuCanal.map((i) => (
                  <option key={i.id} value={i.id}>
                    {i.code} — {i.libelle}
                    {i.chariaCompliant ? ' · charia' : ''}
                  </option>
                ))}
              </select>
              {instruments.chargement ? (
                <span className="champ__aide">Chargement du référentiel…</span>
              ) : instrumentsDuCanal.length === 0 ? (
                <span className="champ__message">
                  Aucun instrument actif de ce type au référentiel.
                </span>
              ) : null}
            </div>
          )}

          <div className="champs-en-ligne">
            {parQuantite ? (
              <Champ
                label="Quantité (titres)"
                montant
                value={saisie.quantiteDemandee}
                onChange={(e) => setSaisie({ ...saisie, quantiteDemandee: e.target.value })}
                placeholder="100"
                required
              />
            ) : (
              <Champ
                label="Montant (MAD)"
                montant
                value={saisie.montantDemande}
                onChange={(e) => setSaisie({ ...saisie, montantDemande: e.target.value })}
                placeholder="300000.00"
                required
              />
            )}
            {parQuantite && (
              <Champ
                label={saisie.canal === 'SALLE_MARCHES' ? 'Prix indicatif (%)' : 'Cours indicatif'}
                montant
                value={saisie.coursIndicatif}
                onChange={(e) => setSaisie({ ...saisie, coursIndicatif: e.target.value })}
                aide="Facultatif — le dernier cours connu sert à défaut."
              />
            )}
          </div>

          <div className="modale__actions">
            <button
              type="button"
              className="bouton bouton--fantome"
              onClick={() => setSaisieOuverte(false)}
            >
              Annuler
            </button>
            <button
              className="bouton"
              type="submit"
              disabled={
                occupe ||
                !saisie.mandatId ||
                (achatBdt ? !saisie.trancheMaturiteId : !saisie.instrumentId) ||
                (parQuantite ? !saisie.quantiteDemandee : !saisie.montantDemande)
              }
            >
              {occupe ? 'Création…' : 'Créer l’ordre'}
            </button>
          </div>
        </form>
      </Modale>

      <ModaleFonds
        ordre={substitution}
        onFermer={() => setSubstitution(null)}
        onConfirme={async () => {
          setSubstitution(null);
          await recharger();
        }}
      />
    </>
  );
}
